<template>
  <div>
    <px-view-header title="Cadastro de compra" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>
    <div v-if="isLoadingInitialData">
      <px-circular-loading/>
    </div>
    <v-stepper v-if="!isLoadingInitialData"
               class="elevation-1 rounded-card"
               v-model="currentStep"
               vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">
        Condições e frete
      </v-stepper-step>
      <v-stepper-content step="1">
        <px-cadastro-compra-dados-basicos :compra="compra"
                                          :is-editing="isEdicao"
                                          :is-from-requisicao="hasAnyItemFromSolicitacao"
                                          :erp-campos-integracao="erpCamposIntegracao"
                                          :campos-obrigatorios="camposObrigatorios"
                                          :campos-readonly="camposReadonly"
                                          :campos-bloqueados-edicao="camposBloqueadosEdicao"
                                          @change-fornecedor="getDetalhesFornecedor"
                                          @cancel="onClickCancel"
                                          @ready="next"/>
      </v-stepper-content>
      <v-stepper-step :complete="currentStep > 2" step="2">Notificações</v-stepper-step>
      <v-stepper-content step="2">
        <px-notificar-fornecedor :compra="compra"
                                 :is-edicao="isEdicao"
                                 :campos-personalizados="camposPersonalizados"
                                 @remove="onRemoveMessage"
                                 @ready="next"
                                 @previous="previous"/>
      </v-stepper-content>
      <v-stepper-step :complete="currentStep > 3" step="3">Produtos</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-compra-produtos usa-tabela-preco
                                     :compra="compra"
                                     :loading="loading"
                                     :hide-acoes="hideAcoes"
                                     :is-editing="isEdicao"
                                     :is-rascunho="isRascunho"
                                     :campos-readonly="camposReadonly"
                                     :campos-obrigatorios="camposObrigatorios"
                                     :erp-campos-integracao="erpCamposIntegracao"
                                     :is-from-requisicao="hasAnyItemFromSolicitacao"
                                     @ready="onClickSalvar"
                                     @previous="previous"
                                     @save-draft="onSaveDraft"/>
      </v-stepper-content>
    </v-stepper>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>

<script>
import { ErpCamposIntegracao } from 'px-business-components';
import { CasaDecimalUtils } from 'px-components';
import CompradorService from '@/api/comprador-service';
import { mapGetters } from 'vuex';
import moedaMixin from '@/mixins/moeda-mixin';
import {
  hasCadastrosAdicionaisErp,
} from '@/modules/carrinho/finalizacao/campos-erp/validate-campos-erp';
import GestaoFornecedorService from '@/modules/gestao-fornecedor/gestao-fornecedor-service';
import {
  buildCadastrosAdicionaisPayload,
  getCadastroAdicional,
} from '@/utils/cadastros-adicionais-utils';
import {
  hasCadastrosAdicionaisProdutoErp,
} from '@/modules/produtos/campos-erp/helpers/validate-campos-erp';
import { parseBooleanQueryParam } from '@/utils/query-param-utils';
import { getTipoAplicacaoIcms, getTipoAplicacaoIpi } from '@/utils/imposto-utils';
import {
  computeInitialCopia,
  computeInitialCopiaAprovacao,
  computeInitialEdicao,
  computeInitialRascunho,
  initialState,
} from '@/modules/compra/cadastro/compra-utils';
import { cloneDeep, isEmpty } from 'lodash';
import {
  currencyDP,
  calcularTotaisCompra,
  calcularTotaisSaving,
  calculatePercentualDescontoProdutos,
  sumSavingProdutos,
} from '@/modules/compra/cadastro/calculo-compra.service';
import EntidadeCamposCompraService from '@/modules/erp-campos/entidade-campos-compra-service';
import AprovacaoCompraService from '@/modules/aprovacao-compra/aprovacao-compra-service';
import { deepNotEquals } from '@/utils/object-utils';
import { filterProdutoNaoHomologado } from '@/utils/fornecedor-produto-utils';
import EntidadeCamposPersonalizadosService
  from '@/modules/campos-personalizados/entidade-campos-personalizados-service';
import { setCamposPersonalizados } from '@/modules/campos-personalizados/campos-personalizado-utils';
import CadastrosAdicionais from '@/enums/cadastros-adicionais';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';
import TipoNotaEntradaService from '@/modules/tipo-nota-entrada/tipo-nota-entrada-service';
import CadastroCompraDadosBasicos from './CadastroCompraDadosBasicos.vue';
import CadastroCompraProdutos from './CadastroCompraProdutos.vue';
import CadastroNotificarFornecedor from './CadastroNotificarFornecedor.vue';
import CompraService from '../compra-service';

export default {
  mixins: [moedaMixin],
  data() {
    return {
      currentStep: 1,
      loading: false,
      loadingCompra: false,
      loadingAprovacao: false,
      camposObrigatorios: null,
      compra: null,
      initialCompra: {},
      isRascunho: parseBooleanQueryParam(this.$route.query.isRascunho),
      isCopy: parseBooleanQueryParam(this.$route.query.isCopy),
      idCompra: this.$route.params.id,
      idAprovacaoCopia: this.$route.query.idAprovacao,
      camposPersonalizados: null,
      camposReadonly: null,
      camposBloqueadosEdicao: null,
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Compras',
          to: '/compras',
        },
        { text: 'Cadastro de compras' },
      ],
    };
  },
  computed: {
    ...mapGetters('entidade', ['idEntidade', 'erpCamposIntegracao']),
    confirmMessage() {
      return this.isRascunho ? 'Você deseja atualizar o rascunho?' : 'Você deseja salvar a compra como rascunho?';
    },
    isEdicao() {
      return this.idCompra && !this.isRascunho && !this.isCopy;
    },
    hasCadastrosAdicionais() {
      return this.compra.cadastrosAdicionais !== null
      || hasCadastrosAdicionaisErp(this.erpCamposIntegracao, this.compra.cadastrosAdicionais);
    },
    hasCadastrosAdicionaisProduto() {
      return produto => produto.cadastrosAdicionais !== null
      || hasCadastrosAdicionaisProdutoErp(
        this.erpCamposIntegracao,
        produto.cadastrosAdicionais,
      );
    },
    isLoadingInitialData() {
      return this.loadingCompra || this.loadingAprovacao || this.loading;
    },
    isFormDirty() {
      if (this.isLoadingInitialData) {
        return false;
      }
      return Object.keys(this.initialCompra)
        .some(key => deepNotEquals(this.compra, this.initialCompra, key));
    },
    hasAnyItemFromSolicitacao() {
      return this.compra.produtos.some(produto => !!produto?.requisicaoProduto?.id);
    },
    hideAcoes() {
      return this.erpCamposIntegracao === ErpCamposIntegracao.keys.SENIOR_MEGA.key
        && this.hasAnyItemFromSolicitacao;
    },
    casaDecimal() {
      const mappedProdutos = this.compra.produtos
        .map(produto => produto.produto)
        .filter(produto => produto);
      return CasaDecimalUtils.getCasaDecimal(mappedProdutos);
    },
  },
  methods: {
    onRemoveMessage(item) {
      this.compra.mensagensPersonalizadas = this.compra
        .mensagensPersonalizadas.filter(mensagem => mensagem.id !== item.id);
    },
    onSaveDraft() {
      this.saveDraft();
    },
    onClickBack() {
      if (this.isEdicao) {
        this.$router.push({ name: 'main.compras' });
        return;
      }
      this.$refs.confirm.open(this.confirmMessage,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS na listagem de compras.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$router.push({ name: 'main.compras' });
            return;
          }
          this.saveDraft();
        });
    },
    onClickCancel() {
      this.$router.push({ name: 'main.compras' });
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    buildCadastrosAdicionaisPayload() {
      if (!this.hasCadastrosAdicionais) {
        return [];
      }
      return buildCadastrosAdicionaisPayload(
        this.compra.cadastrosAdicionais,
        TiposEntidadeCadastroAdicional.keys.COMPRA.key,
      );
    },
    buildCadastrosAdicionaisProdutoPayload(produto) {
      if (!this.hasCadastrosAdicionaisProduto(produto)) {
        return [];
      }
      return buildCadastrosAdicionaisPayload(
        produto.cadastrosAdicionais,
        TiposEntidadeCadastroAdicional.keys.COMPRA_PRODUTO.key,
      );
    },
    buildCompra() {
      const compra = { ...this.compra };

      compra.tipoNotaEntrada = compra.tipoNotaEntrada || null;
      compra.dataNecessidadeEntregaSolicitacao = compra.dataNecessidadeEntregaSolicitacao || null;
      compra.frete.totalTransporte = compra.frete.totalTransporte || 0;
      compra.saving = calcularTotaisSaving(compra).saving;
      compra.produtos = compra.produtos.map(produto => ({
        ...produto,
        impostos: {
          ...produto.impostos,
          tipoAplicacaoIcms: getTipoAplicacaoIcms(produto.impostos.icms),
          tipoAplicacaoIpi: getTipoAplicacaoIpi(produto.impostos.ipi),
        },
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        tipoNotaEntrada: compra.tipoNotaEntrada || null,
      }));
      return {
        ...compra,
        mensagem: compra.mensagem,
        cadastrosAdicionais: this.buildCadastrosAdicionaisPayload(),
      };
    },
    buildDraftCompra() {
      const compra = { ...this.compra };
      compra.tipoNotaEntrada = compra.tipoNotaEntrada || null;
      compra.produtos = compra.produtos.filter(produto => !!produto.produto);
      compra.produtos = compra.produtos.map(produto => ({
        ...produto,
        impostos: {
          ...produto.impostos,
          tipoAplicacaoIcms: getTipoAplicacaoIcms(produto.impostos.icms),
          tipoAplicacaoIpi: getTipoAplicacaoIpi(produto.impostos.ipi),
        },
        cadastrosAdicionais: this.buildCadastrosAdicionaisProdutoPayload(produto),
        tipoNotaEntrada: compra.tipoNotaEntrada,
      }));
      return {
        ...compra,
        mensagem: compra.mensagem,
        cadastrosAdicionais: this.buildCadastrosAdicionaisPayload(),
      };
    },
    getProdutosNaoHomologados() {
      return this.compra.produtos
        .map(p => p.produto)
        .filter(p => filterProdutoNaoHomologado(p, this.compra.fornecedor.id));
    },
    validateSaving() {
      const descontoItens = sumSavingProdutos(this.compra.produtos);
      const descontoGeral = currencyDP(this.compra.saving || 0);
      if (Number(descontoItens) === Number(descontoGeral)) {
        return true;
      }
      if (Number(descontoGeral) < 0.01) {
        this.compra.saving = Number(descontoItens);
        return true;
      }
      return this.$refs.confirm.open('O desconto geral da compra, adicionado na primeira etapa, é diferente do somatório dos descontos dos itens.',
        `Deseja alterar o desconto geral da compra (${this.$options.filters.currency(descontoGeral, this.simboloMoeda, this.casaDecimal)})
         para o somatório dos descontos dos itens (${this.$options.filters.currency(descontoItens, this.simboloMoeda, this.casaDecimal)}) e salvar?`,
        { width: 450 })
        .then((confirm) => {
          if (confirm) {
            this.compra.saving = Number(descontoItens);
            return true;
          }
          return false;
        });
    },
    async onClickSalvar() {
      if (currencyDP(
        calcularTotaisCompra(this.compra, this.compra.produtos).total,
        this.casaDecimal,
      ) <= 0) {
        const errorMessage = 'Total da compra deve ser maior que zero para salvar.';
        this.$toast(errorMessage, { color: 'error' });
        return;
      }
      const isValid = await this.validateSaving();
      if (!isValid) {
        return;
      }
      const produtos = this.getProdutosNaoHomologados();
      if (!isEmpty(produtos)) {
        const errorMessage = produtos.length > 1 ? `Fornecedor da compra não é homologado aos produtos: ${produtos.map(p => p.descricao).join(', ')}` : `Fornecedor da compra não é homologado ao produto: ${produtos[0].descricao.trim()}`;
        this.$toast(errorMessage, { color: 'error' });
        return;
      }
      this.loading = true;
      CompraService.save(this.buildCompra())
        .then(({ data }) => {
          const update = this.idCompra && !this.isCopy;
          const message = update ? 'Compra atualizada com sucesso' : 'Compra criada com sucesso';

          this.$toast(message, { color: 'success' });

          if (data.tipo === 'APROVACAO') {
            this.$router.push({
              name: 'main.aprovacao-compra.detalhes',
              params: {
                id: data.id,
              },
            });
            return;
          }

          this.$router.push({
            name: 'main.compras.detalhes',
            params: { id: data.id },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveDraft() {
      this.loading = true;
      CompraService.saveRascunho(this.buildDraftCompra())
        .then(() => {
          const updateRascunho = this.idCompra && this.isRascunho;
          const message = updateRascunho ? 'Rascunho atualizado com sucesso' : 'Rascunho salvo com sucesso';

          this.$toast(message, { color: 'success' });
          this.$router.push({ name: 'main.compras' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    createCompra(data) {
      if (this.isRascunho) {
        this.compra = computeInitialRascunho(data, this.erpCamposIntegracao);
        this.setInitialCompra();
        return;
      }
      if (this.isCopy) {
        this.compra = computeInitialCopia(data, this.erpCamposIntegracao);
        this.setInitialCompra();
        return;
      }
      this.compra = computeInitialEdicao(data, this.erpCamposIntegracao);
      this.setInitialCompra();
    },
    setInitialCompra() {
      this.initialCompra = cloneDeep(this.compra);
    },
    getCompra() {
      this.loadingCompra = true;
      CompraService.getCompraCompleto(this.idCompra)
        .then((response) => {
          this.createCompra(response.data);
        })
        .finally(() => {
          this.loadingCompra = false;
        });
    },
    getAprovacaoCopia() {
      this.loadingAprovacao = true;
      AprovacaoCompraService.getRecusadas(this.idAprovacaoCopia)
        .then((response) => {
          if (!response.data) {
            this.$toast('Nenhum produto pendente para compra', { color: 'error' });
            this.$router.back();
          }
          this.compra = computeInitialCopiaAprovacao(response.data, this.erpCamposIntegracao);
          this.setInitialCompra();
        })
        .finally(() => {
          this.loadingAprovacao = false;
        });
    },
    beforeWindowUnload(e) {
      if (this.isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    getDetalhesFornecedor(id) {
      return GestaoFornecedorService.getByFornecedor(id)
        .then(({ data }) => {
          // fazer função auxiliar quando tiver mais opções de campos adicionais
          this.cadastrosAdicionais.natureza = getCadastroAdicional(data.cadastrosAdicionais,
            CadastrosAdicionais.keys.NATUREZA.key);
        });
    },
    getCompradorDefaultFaturamento() {
      return CompradorService.getDefaultFaturamento()
        .then((response) => {
          this.compra.comprador = response.data;
        });
    },
    getCompradorDefaultEntrega() {
      return CompradorService.getDefaultEntrega()
        .then((response) => {
          this.compra.locaisEntrega = response.data;
        });
    },
    getNotaEntradaDefault() {
      if (this.compra.tipoNotaEntrada !== null) {
        return Promise.resolve().then(() => {
          this.compra.produtos = this.compra.produtos.map(produto => ({
            ...produto,
            tipoNotaEntrada: produto.tipoNotaEntrada ?? this.compra.tipoNotaEntrada,
          }));
        });
      }
      return TipoNotaEntradaService.getNotaEntradaDefault()
        .then((response) => {
          this.compra.tipoNotaEntrada = response.data || null;
          this.compra.produtos = this.compra.produtos.map(produto => ({
            ...produto,
            tipoNotaEntrada: produto.tipoNotaEntrada ?? this.compra.tipoNotaEntrada,
          }));
        });
    },
    getCamposObrigatorios() {
      EntidadeCamposCompraService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
    getCamposReadonly() {
      EntidadeCamposCompraService.getCamposReadonly(this.erpCamposIntegracao)
        .then((data) => {
          this.camposReadonly = data;
        });
    },
    getCamposPersonalizados() {
      this.loading = true;
      EntidadeCamposPersonalizadosService.getCamposPersonalizadosCompraById(this.idEntidade)
        .then((data) => {
          this.camposPersonalizados = data;
          this.compra = setCamposPersonalizados(this.compra, this.camposPersonalizados);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCamposBloqueadosEdicao() {
      return EntidadeCamposCompraService.getCamposBloqueadosEdicao({
        idEntidade: this.idEntidade,
        erpCamposIntegracao: this.erpCamposIntegracao,
      })
        .then((data) => {
          this.camposBloqueadosEdicao = data;
        });
    },
  },
  watch: {
    currentStep(newVal, oldVal) {
      if (newVal === 3 && oldVal !== 3) {
        calculatePercentualDescontoProdutos(this.compra);
      }
    },
  },
  created() {
    this.getCamposReadonly();
    this.getCamposObrigatorios();
    this.getCamposPersonalizados();
    this.getCamposBloqueadosEdicao();

    window.addEventListener('beforeunload', this.beforeWindowUnload);

    this.compra = { ...initialState(this.erpCamposIntegracao) };

    if (this.idCompra) {
      this.getCompra();
      return;
    }
    if (this.idAprovacaoCopia) {
      this.getAprovacaoCopia();
    }

    this.loadingCompra = true;

    Promise.all([
      this.getCompradorDefaultEntrega(),
      this.getCompradorDefaultFaturamento(),
      this.getNotaEntradaDefault(),
    ])
      .then(() => {
        this.setInitialCompra();
        this.loadingCompra = false;
      });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
  components: {
    pxCadastroCompraDadosBasicos: CadastroCompraDadosBasicos,
    pxCadastroCompraProdutos: CadastroCompraProdutos,
    pxNotificarFornecedor: CadastroNotificarFornecedor,
  },
};
</script>
